import { useState } from 'react';
import { Network, ZoomIn, ZoomOut, Maximize2, Download, Play } from 'lucide-react';

export default function GraphVisualization() {
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);

  return (
    <div className="h-full">
      <div className="mb-6">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Transaction Graph
        </h1>
        <p className="text-gray-400 text-lg">
          Visualize address relationships and fund flows
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Graph Canvas */}
        <div className="lg:col-span-3">
          <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
            {/* Controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-all">
                  <ZoomIn size={18} className="text-gray-300" />
                </button>
                <button className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-all">
                  <ZoomOut size={18} className="text-gray-300" />
                </button>
                <button className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-all">
                  <Maximize2 size={18} className="text-gray-300" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 transition-all flex items-center gap-2 text-white font-medium">
                  <Play size={16} />
                  Animate
                </button>
                <button className="px-4 py-2 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-all flex items-center gap-2 text-gray-300">
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>

            {/* Graph Area */}
            <div className="relative h-[600px] rounded-lg bg-gray-900/50 border border-gray-700 overflow-hidden">
              {/* Demo Graph Visualization */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Network size={64} className="text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">
                    Interactive Graph View
                  </h3>
                  <p className="text-gray-500 mb-4">
                    D3.js graph visualization will appear here
                  </p>
                  <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all">
                    Load Sample Graph
                  </button>
                </div>
              </div>

              {/* Demo Nodes */}
              <svg className="absolute inset-0 w-full h-full opacity-20">
                <defs>
                  <radialGradient id="nodeGradient">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#1d4ed8" />
                  </radialGradient>
                </defs>
                {/* Central node */}
                <circle cx="50%" cy="50%" r="30" fill="url(#nodeGradient)" />
                {/* Connected nodes */}
                <circle cx="30%" cy="30%" r="20" fill="#10b981" opacity="0.6" />
                <circle cx="70%" cy="30%" r="20" fill="#f59e0b" opacity="0.6" />
                <circle cx="30%" cy="70%" r="20" fill="#ef4444" opacity="0.6" />
                <circle cx="70%" cy="70%" r="20" fill="#8b5cf6" opacity="0.6" />
                {/* Connections */}
                <line x1="50%" y1="50%" x2="30%" y2="30%" stroke="#3b82f6" strokeWidth="2" opacity="0.3" />
                <line x1="50%" y1="50%" x2="70%" y2="30%" stroke="#3b82f6" strokeWidth="2" opacity="0.3" />
                <line x1="50%" y1="50%" x2="30%" y2="70%" stroke="#3b82f6" strokeWidth="2" opacity="0.3" />
                <line x1="50%" y1="50%" x2="70%" y2="70%" stroke="#3b82f6" strokeWidth="2" opacity="0.3" />
              </svg>
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-gray-300">Safe</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="text-gray-300">Low Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <span className="text-gray-300">Medium Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-gray-300">High Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                <span className="text-gray-300">Pool</span>
              </div>
            </div>
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          {/* Filters */}
          <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
            <h3 className="text-lg font-semibold text-white mb-4">Filters</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Risk Level
                </label>
                <select className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>All Levels</option>
                  <option>Safe</option>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max Depth
                </label>
                <input
                  type="number"
                  defaultValue={5}
                  className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Min Transactions
                </label>
                <input
                  type="number"
                  defaultValue={10}
                  className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all">
                Apply Filters
              </button>
            </div>
          </div>

          {/* Node Info */}
          <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
            <h3 className="text-lg font-semibold text-white mb-4">Node Details</h3>
            {selectedAddress ? (
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-400 mb-1">Address</div>
                  <div className="text-sm text-white font-mono">0x1234...5678</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Risk Score</div>
                  <div className="text-sm text-white font-semibold">45/100</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Connections</div>
                  <div className="text-sm text-white">12 addresses</div>
                </div>
                <button className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-all">
                  View Full Details
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                Click on a node to view details
              </p>
            )}
          </div>

          {/* Stats */}
          <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
            <h3 className="text-lg font-semibold text-white mb-4">Graph Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Total Nodes</span>
                <span className="text-sm text-white font-semibold">247</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Connections</span>
                <span className="text-sm text-white font-semibold">1,043</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Pools Detected</span>
                <span className="text-sm text-white font-semibold">12</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Avg Hops</span>
                <span className="text-sm text-white font-semibold">4.2</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
